import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Stack,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Divider
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PrintIcon from '@mui/icons-material/Print';

const QR_SIZES = [
  {
    id: 'badge',
    label: 'Étiquette/Badge',
    size: 20,
    description: '2 x 2 cm - Pour badges et étiquettes de clés',
    distance: '20 cm'
  },
  {
    id: 'card',
    label: 'Carte de visite',
    size: 25,
    description: '2,5 x 2,5 cm - Pour cartes de visite',
    distance: '25 cm'
  },
  {
    id: 'document',
    label: 'Document standard',
    size: 40,
    description: '4 x 4 cm - Pour documents et affichages intérieurs',
    distance: '40 cm',
    default: true
  },
  {
    id: 'door',
    label: 'Affichage porte/vitrine',
    size: 70,
    description: '7 x 7 cm - Pour portes et vitrines',
    distance: '70 cm'
  },
  {
    id: 'panel',
    label: 'Panneau extérieur',
    size: 100,
    description: '10 x 10 cm - Pour panneaux extérieurs',
    distance: '1 mètre'
  },
  {
    id: 'large',
    label: 'Panneau grande distance',
    size: 150,
    description: '15 x 15 cm - Visible depuis la route',
    distance: '1,5 mètre'
  }
];

export default function QRCodeDialog({ open, onClose, qrData }) {
  const [qrImage, setQrImage] = useState('');
  const [selectedSize, setSelectedSize] = useState('document');

  useEffect(() => {
    if (qrData?.qrCodeImage) {
      setQrImage(qrData.qrCodeImage);
    }
  }, [qrData]);

  const handlePrint = () => {
    const sizeObj = QR_SIZES.find(s => s.id === selectedSize);
    const printWindow = window.open('', '_blank');

    printWindow.document.write(`
      <html>
        <head>
          <title>QR Code - ${qrData.code}</title>
          <style>
            @page {
              size: A4;
              margin: 20mm;
            }
            body {
              font-family: Arial, sans-serif;
              text-align: center;
              padding: 20px;
              display: flex;
              justify-content: center;
              align-items: center;
              min-height: 100vh;
            }
            .container {
              max-width: 600px;
              margin: 0 auto;
              border: 2px solid #333;
              padding: 20px;
              border-radius: 10px;
              page-break-inside: avoid;
            }
            .qr-image {
              margin: 20px auto;
              width: 100% !important;
              max-width: 500px !important;
              height: auto !important;
              display: block;
            }
            .info { text-align: left; margin: 20px 0; }
            .info p { margin: 5px 0; }
            .size-info {
              margin-top: 20px;
              padding: 10px;
              background: #f5f5f5;
              border-radius: 5px;
              font-size: 11px;
            }
            @media print {
              body {
                padding: 0;
                margin: 0;
              }
              .container {
                border-width: 1px;
                max-width: none;
              }
              .qr-image {
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
              }
            }
          </style>
        </head>
        <body>
          <div class="container">
            <img src="${qrImage}" class="qr-image" />
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    setTimeout(() => {
      printWindow.print();
    }, 500);
  };

  if (!qrData) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">QR Code - {qrData.code}</Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent>
        <Box textAlign="center">
          {qrImage && (
            <img src={qrImage} alt={qrData.code} style={{ width: '100%', maxWidth: 400 }} />
          )}
          <Typography variant="body2" color="text.secondary" mt={2}>
            {qrData.description}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {qrData.location} - {qrData.residenceName}
          </Typography>

          <Divider sx={{ my: 3 }} />

          <Box>
            <Typography variant="subtitle2" gutterBottom align="left">
              Options d'impression
            </Typography>

            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>Taille d'impression</InputLabel>
              <Select
                value={selectedSize}
                onChange={(e) => setSelectedSize(e.target.value)}
                label="Taille d'impression"
              >
                {QR_SIZES.map(size => (
                  <MenuItem key={size.id} value={size.id}>
                    <Box sx={{ py: 0.5 }}>
                      <Typography variant="body2" fontWeight="medium">
                        {size.label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {size.description}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="caption">
                <strong>Distance de scan recommandée :</strong> {
                  QR_SIZES.find(s => s.id === selectedSize)?.distance
                }
              </Typography>
            </Alert>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>
          Annuler
        </Button>
        <Button onClick={handlePrint} startIcon={<PrintIcon />} variant="contained">
          Imprimer
        </Button>
      </DialogActions>
    </Dialog>
  );
}
